import React from "react";
import { type NextPage } from "next";
import { SimpleGrid, Box } from "@chakra-ui/react";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import { FillImage } from "components/images";
import { Section, sectionSpacingY } from "components/section";
import { CenteredQuote } from "components/quotes";
import { MatchBoosterHero, FeatureAccordion } from "components/platform/match-booster/";
import { ComposableCdpFeaturesPlate, DestinationMarquee, ProductDemo } from "components/content";
import { BookDemoButton } from "components/event-tracking";
import {
  desktopOnlyDisplay,
  getLandingPageProps,
  getPageMetaImage,
  getSafeCustomer,
  type LandingPageProps,
} from "utils";
import { TextTitle } from "ui/text";
import { SolidPlate } from "ui/plates";
import { Button } from "ui/button";
import { Diptych, DiptychColumn } from "ui/diptych";
import firstDiptychImage from "../../../public/assets/platform/match-booster/diptych-1.png";
import secondDiptychImage from "../../../public/assets/platform/match-booster/diptych-2.png";
import thirdDiptychImage from "../../../public/assets/platform/match-booster/diptych-3.png";

// Note: This page is used for both /platform/match-booster here and imported by /join/match-booster-liveramp-competitor for a custom ad page
const MatchBooster: NextPage<LandingPageProps> = ({
  noIndex,
  page,
  announcementBanner,
  destinations,
  defaultLogos,
  headerVariant,
  customers,
}) => {
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");

  return (
    <Page
      noIndex={noIndex}
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      headerVariant={headerVariant}
      theme="dark"
      bgColor="new.violet.950"
      footerOptions={{ variant: "violet" }}
    >
      <MatchBoosterHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos customers={defaultLogos} my={0} py={sectionSpacingY} />

      <Section my={0} pb={sectionSpacingY}>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: "biggest", md: "small" }}>
          <TextTitle
            size="xs"
            chip={{ variant: "gray", text: "Performance Marketers" }}
            title="Maximize paid media revenue & ROAS"
            description="Expand LTV, reduce wasted spend, and optimize performance across media channels."
          />

          <TextTitle
            size="xs"
            chip={{ variant: "gray", text: "Publishers" }}
            title="Turn visitors into advertising dollars"
            description="Transform known and anonymous visits into monetizable audiences across publications."
          />

          <TextTitle
            size="xs"
            chip={{ variant: "gray", text: "Media Networks" }}
            title="Increase media monetization & yield"
            description="Enable brands to engage your consumers online, in-person, and across external media channels."
          />
        </SimpleGrid>
      </Section>

      <SolidPlate theme="dark" bgColor="new.violet.950" spotlightColor="new.violet.900">
        <TextTitle
          align="center"
          theme="dark"
          size="lg"
          maxW="780px"
          descriptionMaxW="600px"
          title="Reach and convert more customers in a cookieless world"
          description="Enhance every visit, profile, and event with our proprietary cookieless identity graph built in partnership with the world’s largest and most reputable data providers."
          actions={<BookDemoButton isLink text="Learn more" />}
        />

        <FeatureAccordion />

        <CenteredQuote
          my={sectionSpacingY}
          maxW="890px"
          theme="dark"
          quoteText="Hightouch has provided us with enhanced capabilities compared to our previous data onboarding platform. We were able to achieve much higher match rates across every ad platform."
          company={{
            name: "Weight Watchers",
            logo: weightWatchers?.logoUrl,
          }}
          author={{
            name: "Annie Davis",
            job: (
              <>
                Senior Manager, Paid Social
                <Box as="br" display={desktopOnlyDisplay()} />
              </>
            ),
          }}
        />

        <ProductDemo hasDemoLinkOnly theme="dark" />
      </SolidPlate>

      <Section bg="white">
        <TextTitle
          align="center"
          size="lg"
          maxW="700px"
          descriptionMaxW="600px"
          title="Global privacy & compliance"
          description="Hightouch’s zero-copy architecture and suite of governance tools ensure that you can maintain bullet-proof compliance, regardless of region or law."
          actions={
            <Button hasRightChevron href="/platform/security">
              Learn more
            </Button>
          }
        />

        <DiptychColumn mt={{ base: "biggest", md: "huge" }}>
          <Diptych
            direction="right"
            title="Zero-copy onboarding"
            description="Avoid storing third-party identity data from Match Booster in your infrastructure. Hightouch does not store your first-party data on our premises either—no third-party data storage required."
            actions={
              <Button
                hasRightChevron
                href="/docs/match-booster/overview#modern-warehouse-native-onboarding"
                target="_blank"
              >
                Learn more
              </Button>
            }
            media={
              <FillImage
                src={firstDiptychImage}
                alt="Abstract illustration of a data warehouse with logos from tools like AWS, Google Ads, Facebook, and Bing floating above it."
              />
            }
          />

          <Diptych
            direction="left"
            title="Privacy preferences"
            description="Easily manage opt-outs and data sharing preferences with rule sets that control data access across teams and regions, ensuring compliance with customer data preferences on ad platforms."
            actions={
              <Button hasRightChevron href="/playbooks/managing-global-opt-system" target="_blank">
                Learn more
              </Button>
            }
            media={
              <FillImage
                src={secondDiptychImage}
                alt="Hightouch ui displaying select fields mapping to fields for opt outs, data options, data options country, and data options state."
              />
            }
          />

          <Diptych
            direction="right"
            title="Regional storage & processing"
            description="Ensure compliance with GDPR and other data residency laws by storing, processing, and logging data in the appropriate region."
            actions={
              <Button hasRightChevron href="/docs/security/regions" target="_blank">
                Learn more
              </Button>
            }
            media={
              <FillImage
                src={thirdDiptychImage}
                alt="Abstract illustration of a globe with three data warehouses on top of it, and cyclical lines running through them. It represents data residency and compliance on a global scale, for any region."
              />
            }
          />
        </DiptychColumn>
      </Section>

      <ComposableCdpFeaturesPlate
        theme="dark"
        bgColor="new.violet.900"
        title="Match Booster is powered by your cloud data warehouse"
      />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export default MatchBooster;

export const getStaticProps = getLandingPageProps("/platform/match-booster", {
  loadDefaultLogos: true,
  loadDestinations: "all",
  loadCustomers: ["weight-watchers"],
});
